/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox } from 'components/selects/ComboBox';
import { IconButton } from 'components/buttons/IconSVG';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { Code } from 'models/common/CommonCode';
import { findProject } from 'apis/setup/SetupProject';
import { CommonUtil } from 'utils/CommonUtil';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import {
  SetupProjectManager,
  SetupProjectManagerCondition,
} from 'models/setup/SetupProjectManager';
import { findSetupProjectManager } from 'apis/setup/SetupProjectManager';
import useSessionStore from '../../stores/useSessionStore';

const SetupProjectManagerPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const userSession = useSessionStore();

  const gridRef = useRef();

  const [rowData, setRowData] = useState<SetupProjectManager[]>([]);

  const [userModalMode, setUserModalMode] = useState<string>('');

  const initCondition = {
    bildPlntCds: [],
    elmPrjId: '',
  };

  const [setupProjectCondition, setSetupProjectCondition] = useState<SetupProjectManagerCondition>({
    ...initCondition,
  });

  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);

  const [prjMasterData, setPrjMasterData] = useState<SetupProjectManager[]>([]);

  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (setupProjectCondition?.bildPlntCds || []).length > 0
          ? setupProjectCondition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, setupProjectCondition?.bildPlntCds]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');

    setBildPlntCode(bildPlntCode);

    findProject({ searchVisibleSaveYn: 'N' }).then((result: SetupProjectManager[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        width: 40,
        minWidth: 40,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'bildPlntNm',
        header: String(t('setup.label.플랜트', '플랜트')),
        width: 150,
        minWidth: 150,
        align: 'center',
      },
      {
        binding: 'elmPrjNm',
        header: String(t('setup.label.프로젝트명', '프로젝트명')),
        width: 350,
        minWidth: 210,
        align: 'left',
      },
      {
        binding: 'wktTpCdNm',
        header: String(t('setup.label.구분', '구분')),
        width: 150,
        minWidth: 150,
        align: 'left',
      },
      {
        binding: 'mgrNms',
        header: String(t('setup.label.담당자', '담당자')),
        width: '*',
        minWidth: 210,
        align: 'left',
      },
    ];
  }, [rowData]);

  const handleOnChange = (name, value) => {
    setSetupProjectCondition((prev) => ({ ...prev, [name]: value }));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnSearch = (elmPrjId?: string) => {
    openLoading(true);
    findSetupProjectManager({
      ...setupProjectCondition,
      elmPrjId: elmPrjId || setupProjectCondition?.elmPrjId,
    })
      .then((result: SetupProjectManager[]) => {
        if (result !== null) {
          setRowData(result);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.프로젝트 담당자 목록', '프로젝트 담당자 목록'),
      t('setup.label.프로젝트 담당자 List', '프로젝트 담당자 List')
    );
  };

  const callbackUserData = (callbackData) => {
    if (userModalMode === 'searchSuHqMgrId') {
      setSetupProjectCondition({
        ...setupProjectCondition,
        searchSuHqMgrId: callbackData?.userId || '',
        mgrNm: callbackData?.empNm || '',
      });
    }
  };

  useEffect(() => {
    initCodes();
    if (userSession?.elmPrjId) {
      handleOnChange('elmPrjId', userSession?.elmPrjId);
    }
    btnSearch(userSession?.elmPrjId);
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupProjectCondition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          setupProjectCondition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.프로젝트명', '프로젝트명'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsPrjMaster}
                  defaultValue={setupProjectCondition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.담당자', '담당자'))}</label>
                <CustomInputWithSearch
                  name="userId"
                  value={setupProjectCondition.mgrNm || ''}
                  placeholder={String(
                    t('com.label.담당자를 검색 하세요.', '담당자를 검색 하세요.')
                  )}
                  onChange={(e) => handleOnChange('mgrNm', e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setSetupProjectCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.프로젝트 담당자 목록', '프로젝트 담당자 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={false}
        isReadOnly={true}
        allowPinning={false}
        height={600}
        initialized={onInitialized}
      />
    </>
  );
};

export default SetupProjectManagerPage;
